import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Minus, Plus, Sparkles, Clock } from "lucide-react";
import PhoneShell from "../components/PhoneShell";
import { getPhone, getName, getRef, consumeRef, getCart, setCart, updateQty, cartKey, cartSubtotal, addToCart, customLabel, getNotifyOk } from "../lib/session";
import { api } from "../lib/api";
import { toast } from "sonner";
import OtpModal from "../components/OtpModal";

const pickupOptions = [7, 10, 15, 20, 25, 30];
const tipOptions = [
    { label: "No tip", val: 0 },
    { label: "10%", val: 0.1 },
    { label: "15%", val: 0.15 },
    { label: "20%", val: 0.2 },
];

const upsellCandidates = [
    { id: "blueberry_muffin", name: "Blueberry Muffin", price: 4.5, kind: "food" },
    { id: "almond_croissant", name: "Almond Croissant", price: 5.5, kind: "food" },
    { id: "banana_bread", name: "Banana Bread", price: 5.0, kind: "food" },
];

const Cart = () => {
    const nav = useNavigate();
    const { slug } = useParams();
    const phone = getPhone();
    const [items, setItems] = useState(getCart());
    const [loyalty, setLoyalty] = useState({ stamps: 0, free_coffee_available: false });
    const [pickup, setPickup] = useState(7);
    const [tipPct, setTipPct] = useState(0);
    const [placing, setPlacing] = useState(false);
    const [otpOpen, setOtpOpen] = useState(false);
    // Kept so we can resume placeOrder(method) after the OTP modal reports success.
    const [pendingMethod, setPendingMethod] = useState(null);

    useEffect(() => {
        if (!phone) {
            nav(`/${slug}`);
            return;
        }
        api.get(`/loyalty/${phone}`).then((r) => setLoyalty(r.data));
    }, [phone, nav, slug]);

    const subtotal = cartSubtotal(items);

    // Compute cheapest coffee discount preview
    const preview = useMemo(() => {
        const coffeePrices = items
            .filter((i) => i.kind === "coffee")
            .flatMap((i) => Array(i.qty).fill(i.price));
        const freeDiscount =
            loyalty.free_coffee_available && coffeePrices.length > 0
                ? Math.min(...coffeePrices)
                : 0;
        const afterFree = subtotal - freeDiscount;
        const tip = afterFree * tipPct;
        const total = afterFree + tip;
        return {
            freeDiscount,
            tipAmount: Math.round(tip * 100) / 100,
            total: Math.max(Math.round(total * 100) / 100, 0),
        };
    }, [items, tipPct, subtotal, loyalty.free_coffee_available]);

    const change = (key, delta) => {
        const found = items.find((i) => cartKey(i) === key);
        const newQty = (found?.qty || 0) + delta;
        const updated = updateQty(key, newQty);
        setItems(updated);
    };

    const hasFood = items.some((i) => i.kind === "food");
    const upsell = useMemo(() => {
        if (hasFood || items.length === 0) return null;
        const picks = upsellCandidates.filter(
            (c) => !items.some((i) => i.id === c.id)
        );
        return picks[Math.floor(Math.random() * picks.length)] || null;
    }, [items, hasFood]);

    const addUpsell = () => {
        if (!upsell) return;
        const updated = addToCart({ ...upsell, size: null, qty: 1 });
        setItems(updated);
        toast.success(`Added ${upsell.name}`);
    };

    const placeOrder = async (method) => {
        if (items.length === 0) return;
        // Gate on Twilio OTP: verify the phone once, then proceed. Successful
        // verifications persist server-side so repeat orders skip this step.
        // If Twilio isn't configured (501) OtpModal auto-resolves and we fall
        // through to the actual order call.
        try {
            const s = await api.get(`/verify/status/${phone}`);
            if (s.data?.twilio_configured && !s.data?.verified) {
                setPendingMethod(method);
                setOtpOpen(true);
                return;
            }
        } catch (_) {
            // If the status check itself fails, don't hold up the order.
        }
        return submitOrder(method);
    };

    const onOtpVerified = () => {
        setOtpOpen(false);
        const m = pendingMethod;
        setPendingMethod(null);
        if (m) submitOrder(m);
    };

    const submitOrder = async (method) => {
        setPlacing(true);
        try {
            const payload = {
                phone,
                name: getName() || null,
                ref: getRef() || null,
                notify_ok: getNotifyOk(),
                items: items.map((i) => ({
                    id: i.id,
                    name: i.name,
                    kind: i.kind,
                    size: i.size || null,
                    qty: i.qty,
                    price: i.price,
                })),
                pickup_minutes: pickup,
                tip: preview.tipAmount,
                payment_method: method,
                origin_url: window.location.origin,
            };
            const r = await api.post("/orders", payload);
            if (method === "stripe" && r.data.checkout_url) {
                localStorage.setItem("c7_pending_order", r.data.order_id);
                window.location.href = r.data.checkout_url;
                return;
            }
            // Counter payment: clear cart and go to confirmation
            setCart([]);
            consumeRef();
            nav(`/${slug}/confirmation/${r.data.order_id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not place order");
            setPlacing(false);
        }
    };

    if (items.length === 0) {
        return (
            <PhoneShell>
                <div className="px-5 pt-8 pb-16 flex flex-col min-h-screen">
                    <div className="flex items-center gap-3 mb-8">
                        <button
                            data-testid="cart-back"
                            onClick={() => nav(`/${slug}/menu`)}
                            className="h-10 w-10 rounded-full bg-white border border-[#E8DCCB] flex items-center justify-center"
                        >
                            <ArrowLeft className="h-4 w-4" strokeWidth={2.5} />
                        </button>
                        <h1 className="font-display text-3xl font-black">Your cart</h1>
                    </div>
                    <div className="flex-1 flex flex-col items-center justify-center text-center gap-4">
                        <div className="text-6xl">🥐</div>
                        <div className="font-display text-2xl font-bold">Empty cup, empty cart</div>
                        <div className="text-[#8C7A72]">Head back and pick your fix.</div>
                        <button
                            data-testid="cart-empty-back"
                            onClick={() => nav(`/${slug}/menu`)}
                            className="mt-4 px-8 py-4 bg-[#FF4F18] text-white rounded-full font-display font-bold"
                        >
                            Browse menu
                        </button>
                    </div>
                </div>
            </PhoneShell>
        );
    }

    return (
        <PhoneShell>
            <div className="pb-56">
                {/* Header */}
                <div className="px-5 pt-6 pb-3 flex items-center gap-3">
                    <button
                        data-testid="cart-back"
                        onClick={() => nav(`/${slug}/menu`)}
                        className="h-10 w-10 rounded-full bg-white border border-[#E8DCCB] flex items-center justify-center active:scale-95"
                    >
                        <ArrowLeft className="h-4 w-4" strokeWidth={2.5} />
                    </button>
                    <h1 className="font-display text-3xl font-black">Your cart</h1>
                </div>

                {/* Items */}
                <div className="px-5 mt-2 flex flex-col gap-3">
                    <AnimatePresence>
                        {items.map((i) => {
                            const k = cartKey(i);
                            return (
                                <motion.div
                                    key={k}
                                    layout
                                    initial={{ opacity: 0, x: -8 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 8, height: 0 }}
                                    className="bg-white rounded-2xl p-4 border border-[#E8DCCB]/60 flex items-center gap-3"
                                    data-testid={`cart-item-${i.id}`}
                                >
                                    <div className="flex-1">
                                        <div className="font-display font-bold text-base leading-tight">
                                            {i.name}
                                        </div>
                                        {i.size && (
                                            <div className="text-xs text-[#8C7A72] mt-0.5">
                                                {i.size === "L" ? "Large" : "Small"} · $
                                                {i.price.toFixed(2)} each
                                            </div>
                                        )}
                                        {i.customizations && customLabel(i.customizations) && (
                                            <div
                                                className="text-xs text-[#FF4F18] mt-0.5 font-semibold"
                                                data-testid={`cart-custom-${i.id}`}
                                            >
                                                {customLabel(i.customizations)}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            data-testid={`item-minus-${i.id}`}
                                            onClick={() => change(k, -1)}
                                            className="h-8 w-8 rounded-full bg-[#F9F6F0] border border-[#E8DCCB] flex items-center justify-center active:scale-90"
                                        >
                                            <Minus className="h-3 w-3" strokeWidth={3} />
                                        </button>
                                        <div className="w-6 text-center font-display font-black">
                                            {i.qty}
                                        </div>
                                        <button
                                            data-testid={`item-plus-${i.id}`}
                                            onClick={() => change(k, 1)}
                                            className="h-8 w-8 rounded-full bg-[#F9F6F0] border border-[#E8DCCB] flex items-center justify-center active:scale-90"
                                        >
                                            <Plus className="h-3 w-3" strokeWidth={3} />
                                        </button>
                                    </div>
                                    <div className="w-16 text-right font-display font-black">
                                        ${(i.price * i.qty).toFixed(2)}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                </div>

                {/* Upsell */}
                {upsell && (
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mx-5 mt-4 rounded-3xl p-4 bg-[#2C1A12] text-white border border-[#2C1A12] relative overflow-hidden"
                        data-testid="upsell-card"
                    >
                        <div className="absolute -right-6 -bottom-6 h-24 w-24 rounded-full bg-[#FF4F18]/30" />
                        <div className="relative">
                            <div className="flex items-center gap-1.5 text-[#FF4F18] text-xs uppercase tracking-[0.25em] font-bold">
                                <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} /> Complete the combo
                            </div>
                            <div className="mt-2 font-display font-bold text-lg">
                                Add {upsell.name}?
                            </div>
                            <div className="text-white/70 text-sm">
                                Pairs perfectly with your coffee · ${upsell.price.toFixed(2)}
                            </div>
                            <button
                                data-testid="upsell-add"
                                onClick={addUpsell}
                                className="mt-3 px-4 py-2 rounded-full bg-white text-[#2C1A12] font-display font-bold text-sm active:scale-95"
                            >
                                Yes, add it
                            </button>
                        </div>
                    </motion.div>
                )}

                {/* Pickup selector */}
                <div className="px-5 mt-8">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold mb-3">
                        <Clock className="h-3.5 w-3.5" strokeWidth={2.5} />
                        Pickup
                    </div>
                    <div className="flex gap-2 overflow-x-auto hide-scroll -mx-5 px-5">
                        {pickupOptions.map((m) => (
                            <button
                                key={m}
                                data-testid={`pickup-${m}`}
                                onClick={() => setPickup(m)}
                                className={`flex-shrink-0 px-5 py-3 rounded-2xl font-display font-bold border-2 transition-all ${
                                    pickup === m
                                        ? "border-[#FF4F18] bg-[#FF4F18] text-white"
                                        : "border-[#E8DCCB] bg-white text-[#2C1A12]"
                                }`}
                            >
                                {m === 7 ? "Ready in 7 min" : `${m} min`}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tip selector */}
                <div className="px-5 mt-8">
                    <div className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold mb-3">
                        Tip your barista
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                        {tipOptions.map((t) => (
                            <button
                                key={t.label}
                                data-testid={`tip-${t.val}`}
                                onClick={() => setTipPct(t.val)}
                                className={`py-3 rounded-2xl font-display font-bold text-sm border-2 transition-all ${
                                    tipPct === t.val
                                        ? "border-[#FF4F18] bg-[#FF4F18] text-white"
                                        : "border-[#E8DCCB] bg-white text-[#2C1A12]"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Totals */}
                <div className="px-5 mt-8">
                    <div className="rounded-3xl bg-white border border-[#E8DCCB] p-5 flex flex-col gap-2">
                        <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
                        {preview.freeDiscount > 0 && (
                            <Row
                                label="Free coffee reward"
                                value={`-$${preview.freeDiscount.toFixed(2)}`}
                                testid="free-coffee-row"
                                highlight
                            />
                        )}
                        {preview.tipAmount > 0 && (
                            <Row label="Tip" value={`$${preview.tipAmount.toFixed(2)}`} />
                        )}
                        <div className="border-t border-[#E8DCCB] mt-2 pt-3 flex justify-between items-baseline">
                            <div className="text-xs uppercase tracking-[0.25em] font-bold">
                                Total
                            </div>
                            <div
                                className="font-display font-black text-3xl"
                                data-testid="total-amount"
                            >
                                ${preview.total.toFixed(2)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sticky checkout footer */}
            <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-[#F9F6F0]/95 backdrop-blur border-t border-[#E8DCCB] p-4 pb-6 z-20 flex flex-col gap-2">
                <button
                    data-testid="pay-stripe-btn"
                    disabled={placing}
                    onClick={() => placeOrder("stripe")}
                    className="w-full py-4 rounded-full bg-[#FF4F18] text-white font-display font-bold text-lg active:scale-[0.98] shadow-[0_12px_32px_rgba(255,79,24,0.32)] disabled:opacity-60"
                >
                    {placing ? "Placing…" : `Pay & place order · $${preview.total.toFixed(2)}`}
                </button>
                <button
                    data-testid="pay-counter-btn"
                    disabled={placing}
                    onClick={() => placeOrder("counter")}
                    className="w-full py-3 rounded-full bg-white text-[#2C1A12] border-2 border-[#E8DCCB] font-display font-bold text-sm active:scale-[0.98] disabled:opacity-60"
                >
                    Pay at counter
                </button>
            </div>
            <OtpModal
                open={otpOpen}
                phone={phone}
                onVerified={onOtpVerified}
                onCancel={() => {
                    setOtpOpen(false);
                    setPendingMethod(null);
                }}
            />
        </PhoneShell>
    );
};

const Row = ({ label, value, testid, highlight }) => (
    <div className="flex justify-between text-sm" data-testid={testid}>
        <div className={highlight ? "text-[#FF4F18] font-semibold" : "text-[#8C7A72]"}>
            {label}
        </div>
        <div className={highlight ? "text-[#FF4F18] font-bold" : "font-semibold"}>{value}</div>
    </div>
);

export default Cart;
